import { useNavigate, Link } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useTheme } from '@/components/ThemeProvider';
import { CARD_THEMES } from '@/components/cardThemes';
import { CalendarDays, Wallet, Users, Bell, Globe, Shield, ArrowRight, Check, X, Sun, Moon, ChevronDown, ChevronLeft, ChevronRight, Zap, Crown, Gem, ZoomIn, FileUp, HelpCircle, Mail, Send, Building2, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = API.replace('/api', '');

const SCREENSHOTS = [
  { src: '/mockup-dashboard.png', label: 'Дашборд' },
  { src: '/mockup-calendar.png', label: 'Календарь' },
  { src: '/mockup-finance.png', label: 'Финансы' },
  { src: '/mockup-patients.png', label: 'Клиенты' },
];

const FEATURES = [
  { icon: CalendarDays, title: 'Умный календарь', desc: 'Онлайн-запись и синхронизация с Google Calendar: сервис учитывает занятость и добавляет встречи в ваш основной календарь.' },
  { icon: Users, title: 'Картотека клиентов', desc: 'Все клиенты, их история визитов, индивидуальные цены и заметки — в одном месте. Не нужен блокнот.' },
  { icon: Wallet, title: 'Финансы и налоги', desc: 'Доходы, расходы, чистая прибыль. Автоматический расчёт НПД. Контроль должников и депозитов клиентов.' },
  { icon: Bell, title: 'Уведомления', desc: 'Клиент получает напоминание за 24 часа. Вы — утреннюю сводку дня. Меньше неявок, больше порядка.' },
  { icon: Globe, title: 'Персональная визитка', desc: 'Готовый мини-сайт для записи. Отправьте ссылку — клиент сам выберет время. Никаких «а когда вам удобно?».' },
  { icon: Shield, title: 'Всё под контролем', desc: 'Неявки, переносы, отмены — каждый статус на виду. Гибкое расписание с исключениями на любой день.' },
];

const TIERS = [
  { id: 'start', icon: Zap, color: 'text-stone-400', name: 'Старт', price: 'Бесплатно', period: '',
    desc: 'Для знакомства с платформой',
    features: [
      { text: 'До 20 клиентов в базе', ok: true },
      { text: 'До 5 рабочих часов в неделю', ok: true },
      { text: 'Полноценный календарь', ok: true },
      { text: 'Визитка для записи', ok: true },
      { text: '0 администраторов', ok: true },
      { text: 'Финансовая аналитика', ok: false },
      { text: 'Защищённый мессенджер', ok: false },
      { text: 'Свой домен', ok: false },
    ] },
  { id: 'practic', icon: Crown, color: 'text-amber-500', name: 'Практик', price: '690', period: 'руб/мес',
    desc: 'Для частного специалиста',
    features: [
      { text: 'Безлимит клиентов', ok: true },
      { text: 'До 20 рабочих часов в неделю', ok: true },
      { text: 'Финансы: доходы, расходы, налоги', ok: true },
      { text: 'Хранилище файлов', ok: true },
      { text: '1 администратор', ok: true },
      { text: 'Уведомления клиентам', ok: true },
      { text: 'Защищённый мессенджер', ok: false },
      { text: 'Свой домен', ok: false },
    ] },
  { id: 'profi', icon: Gem, color: 'text-violet-400', name: 'Профи', price: '1 690', period: 'руб/мес', popular: true,
    desc: 'Для кабинета с командой',
    features: [
      { text: 'Всё из «Практика»', ok: true },
      { text: 'До 3 администраторов', ok: true },
      { text: 'Защищённый мессенджер', ok: true },
      { text: 'Свой домен', ok: true },
      { text: 'Онлайн-оплата', ok: true },
      { text: 'SMS-уведомления', ok: true },
      { text: 'Лист ожидания', ok: true },
    ] },
  { id: 'clinic', icon: Building2, color: 'text-teal-400', name: 'Организация', price: 'По запросу', period: '', isClinic: true,
    desc: 'Для клиник, салонов и учебных центров',
    features: [
      { text: 'Безлимит специалистов и администраторов', ok: true },
      { text: 'Разделение доходов между специалистами', ok: true },
      { text: 'Управление кабинетами', ok: true },
      { text: 'Защищённый мессенджер', ok: true },
      { text: 'Свой домен и брендинг', ok: true },
      { text: 'Персональный менеджер', ok: true },
    ] },
];

const FAQ = [
  { q: 'Для кого ProPraxis?', a: 'Для психологов, репетиторов, коучей и всех, кто ведёт частную практику с индивидуальными встречами.' },
  { q: 'Нужно ли платить, чтобы начать?', a: 'Нет. Тариф «Старт» бесплатный навсегда. Достаточно для начала практики — до 20 клиентов и 5 часов записей в неделю.' },
  { q: 'Как клиенты записываются?', a: 'Вы отправляете ссылку (или QR-код). Клиент сам выбирает формат, дату и время из вашего свободного расписания.' },
  { q: 'Можно ли перенести запись?', a: 'Да. И вы, и клиент. Клиент может перенести по той же ссылке — но не менее чем за 24 часа до сеанса.' },
  { q: 'Как ProPraxis использует Google Calendar?', a: 'После вашего согласия сервис проверяет только занятые интервалы основного календаря и управляет событиями, созданными для записей ProPraxis. Названия и описания сторонних событий не загружаются.' },
  { q: 'Безопасны ли данные?', a: 'Каждый специалист видит только свои данные. Данные клиентов не передаются третьим лицам.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [openFaq, setOpenFaq] = useState(null);
  const [slide, setSlide] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [specialists, setSpecialists] = useState([]);
  const [disabledPlans, setDisabledPlans] = useState([]);
  const [closedBeta, setClosedBeta] = useState(false);
  const [betaRecruiting, setBetaRecruiting] = useState(false);
  const [betaModalOpen, setBetaModalOpen] = useState(false);
  const [betaEmail, setBetaEmail] = useState('');
  const [betaPhone, setBetaPhone] = useState('');
  const [betaTelegram, setBetaTelegram] = useState('');
  const [betaName, setBetaName] = useState('');
  const [betaSubmitted, setBetaSubmitted] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(() => localStorage.getItem('pp_cookie_consent') === 'true');
  const [supportOpen, setSupportOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesForm, setSalesForm] = useState({ name: '', phone: '', email: '' });
  const [salesSubmitted, setSalesSubmitted] = useState(false);
  const isLoggedIn = document.cookie.includes('pp_csrf');

  const nextSlide = () => setSlide((s) => (s + 1) % SCREENSHOTS.length);
  const prevSlide = () => setSlide((s) => (s - 1 + SCREENSHOTS.length) % SCREENSHOTS.length);

  useEffect(() => {
    api.get(`/public/featured-specialists`).then(({ data }) => setSpecialists(Array.isArray(data) ? data : [])).catch(() => {});
    api.get(`/public/config`).then(({ data }) => {
      setDisabledPlans(Array.isArray(data?.disabled_plans) ? data.disabled_plans : []);
      setClosedBeta(data?.closed_beta || false);
      setBetaRecruiting(data?.beta_recruiting || false);
    }).catch(() => {});
  }, []);

  // Keyboard control for the image lightbox (Esc to close, arrows to navigate)
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowLeft') setLightbox((i) => (i - 1 + SCREENSHOTS.length) % SCREENSHOTS.length);
      else if (e.key === 'ArrowRight') setLightbox((i) => (i + 1) % SCREENSHOTS.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  const visibleTiers = TIERS.filter(t => t.id === 'start' || !disabledPlans.includes(t.id));
  const selfServeTiers = visibleTiers.filter(t => !t.isClinic);   // Старт / Практик / Профи → 3-col grid
  const clinicTier = visibleTiers.find(t => t.isClinic);          // Организация → full-width band

  const handleCTA = () => {
    if (closedBeta || betaRecruiting) setBetaModalOpen(true);
    else navigate('/register');
  };

  const submitBetaSignup = async () => {
    if (!betaEmail.trim() && !betaPhone.trim() && !betaTelegram.trim()) {
      toast.error('Укажите хотя бы один контакт');
      return;
    }
    try {
      await api.post('/public/beta-signup', { email: betaEmail, phone: betaPhone, telegram: betaTelegram, name: betaName });
      setBetaSubmitted(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
  };

  return (
    <div style={{ background: 'var(--app-bg)', color: 'var(--app-text)' }} className="min-h-screen">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b" style={{ background: 'var(--app-sidebar)', borderColor: 'var(--app-border)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <img src="/logo.png" alt="ProPraxis" className="w-12 h-12 rounded-lg object-cover" />
            <span className="font-heading text-base" style={{ color: 'var(--app-text)' }}>
              <span style={{ color: 'var(--app-text-secondary)' }}>Pro</span><span style={{ color: 'var(--app-accent)' }}>Praxis</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="p-2 rounded-lg transition-colors"
              aria-label={theme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'}
              style={{ color: 'var(--app-text-muted)' }}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {isLoggedIn ? (
              <Button size="sm" onClick={() => navigate('/dashboard')}
                className="text-sm text-white" style={{ background: 'var(--app-accent)' }}>В кабинет</Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => navigate('/login')}
                  className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Войти</Button>
                <Button size="sm" onClick={handleCTA}
                  className="text-sm text-white" style={{ background: 'var(--app-accent)' }}>{closedBeta ? 'Запросить доступ' : 'Начать бесплатно'}</Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Closed beta banner */}
      {(closedBeta || betaRecruiting) && (
        <div className="fixed top-14 left-0 right-0 z-40 text-center py-2 px-4 text-xs font-medium cursor-pointer" onClick={() => setBetaModalOpen(true)}
          style={{ background: 'var(--app-accent)', color: 'white' }}>
          {betaRecruiting ? 'Набор в закрытое бета-тестирование — получите 3 месяца Практика бесплатно!' : 'Закрытое бета-тестирование — регистрация по приглашению'}
        </div>
      )}

      {/* Hero */}
      <section className={`${closedBeta || betaRecruiting ? 'pt-40' : 'pt-32'} pb-20 px-4 sm:px-6 text-center`}>
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs mb-7 font-medium"
            style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)' }}>
            Для психологов и репетиторов
          </div>
          <h1 className="font-heading mb-7" style={{ color: 'var(--app-text)', fontSize: 'clamp(2.75rem, 7vw, 5rem)', lineHeight: 1.04, letterSpacing: '-0.02em' }}>
            Вся частная практика —<br />
            <span style={{ color: 'var(--app-accent)' }}>в одном приложении</span>
          </h1>
          <p className="mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--app-text-secondary)', fontSize: 'clamp(1.0625rem, 2vw, 1.25rem)' }}>
            Расписание, записи клиентов, финансы и персональная визитка. Автоматизируйте рутину — сфокусируйтесь на работе с людьми.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="lg" onClick={handleCTA}
              className="text-white px-8 text-base font-semibold" style={{ background: 'var(--app-accent)', height: '3.25rem' }}>
              Начать бесплатно <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-base border-2 hover:bg-white/5"
              style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)', height: '3.25rem' }}>
              Узнать больше
            </Button>
          </div>
          <p className="text-sm mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1" style={{ color: 'var(--app-text-muted)' }}>
            <span className="inline-flex items-center gap-1.5"><Check size={14} style={{ color: 'var(--app-accent)' }} /> Бесплатно навсегда</span>
            <span className="inline-flex items-center gap-1.5"><Check size={14} style={{ color: 'var(--app-accent)' }} /> Без карты</span>
            <span className="inline-flex items-center gap-1.5"><Check size={14} style={{ color: 'var(--app-accent)' }} /> За 30 секунд</span>
          </p>

          {/* Product carousel */}
          <div className="mt-14 relative mx-auto max-w-4xl">
            <div className="relative overflow-hidden rounded-xl shadow-2xl shadow-black/40 border"
              style={{ borderColor: 'var(--app-border)' }}>
              <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${slide * 100}%)` }}>
                {SCREENSHOTS.map((s, i) => (
                  <div key={i} className="w-full shrink-0 cursor-zoom-in relative group" onClick={() => setLightbox(i)}>
                    <img src={s.src} alt={`Скриншот: ${s.label}`} className="w-full" draggable={false}
                      loading={i === 0 ? 'eager' : 'lazy'} decoding="async" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <ZoomIn size={32} className="text-white/80" />
                    </div>
                  </div>
                ))}
              </div>
              {/* Nav arrows */}
              <button onClick={(e) => { e.stopPropagation(); prevSlide(); }} aria-label="Предыдущий скриншот"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'var(--app-card)', color: 'var(--app-text)', border: '1px solid var(--app-border)' }}>
                <ChevronLeft size={18} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); nextSlide(); }} aria-label="Следующий скриншот"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'var(--app-card)', color: 'var(--app-text)', border: '1px solid var(--app-border)' }}>
                <ChevronRight size={18} />
              </button>
            </div>
            {/* Dots + label */}
            <div className="flex items-center justify-center gap-3 mt-4">
              {SCREENSHOTS.map((s, i) => (
                <button key={i} onClick={() => setSlide(i)}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${i === slide ? 'font-medium' : ''}`}
                  style={{
                    background: i === slide ? 'var(--app-accent-muted)' : 'transparent',
                    color: i === slide ? 'var(--app-accent)' : 'var(--app-text-faint)',
                  }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Lightbox */}
          {lightbox !== null && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setLightbox(null)}
              role="dialog" aria-modal="true" aria-label={`Скриншот: ${SCREENSHOTS[lightbox].label}`}
              style={{ background: 'rgba(0,0,0,0.85)' }}>
              <button aria-label="Закрыть" className="absolute top-4 right-4 text-white/60 hover:text-white text-3xl" onClick={() => setLightbox(null)}>
                &times;
              </button>
              <button aria-label="Предыдущий скриншот" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + SCREENSHOTS.length) % SCREENSHOTS.length); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20">
                <ChevronLeft size={22} />
              </button>
              <img src={SCREENSHOTS[lightbox].src} alt={`Скриншот: ${SCREENSHOTS[lightbox].label}`} className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
              <button aria-label="Следующий скриншот" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % SCREENSHOTS.length); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-white/20">
                <ChevronRight size={22} />
              </button>
              <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">{SCREENSHOTS[lightbox].label}</p>
            </div>
          )}
        </div>
      </section>

      {/* Problem → Solution */}
      <section className="py-16 px-4 sm:px-6" style={{ background: 'var(--app-card)' }}>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl mb-4" style={{ color: 'var(--app-text)' }}>Знакомо?</h2>
            <ul className="space-y-3">
              {['Расписание в блокноте и голове', 'Клиент пишет «А когда можно?» — и начинается переписка', 'Забыли напомнить — неявка', 'В конце месяца непонятно, сколько заработали'].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <X size={16} className="text-red-400 mt-0.5 shrink-0" />
                  <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl mb-4" style={{ color: 'var(--app-accent)' }}>С ProPraxis</h2>
            <ul className="space-y-3">
              {['Интерактивный календарь с drag-and-drop', 'Клиент записывается сам по вашей ссылке', 'Автоматическое напоминание за 24 часа', 'Дашборд: доход, расход, налог — одним взглядом'].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check size={16} className="shrink-0 mt-0.5" style={{ color: 'var(--app-accent)' }} />
                  <span className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Smart Import Teaser */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl border" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
            <div className="relative p-6 sm:p-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
              {/* Icon */}
              <div className="shrink-0">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center relative" style={{ background: 'var(--app-accent-muted)' }}>
                  <FileUp size={36} strokeWidth={1.5} style={{ color: 'var(--app-accent)' }} />
                  <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: 'var(--app-accent)' }}>ИИ</div>
                </div>
              </div>
              {/* Text */}
              <div className="flex-1 text-center sm:text-left">
                <Badge className="mb-2 text-[10px] font-semibold uppercase tracking-wider" style={{ background: 'var(--app-accent-muted)', color: 'var(--app-accent)', border: 'none' }}>
                  Новинка
                </Badge>
                <h3 className="font-heading text-xl sm:text-2xl mb-2" style={{ color: 'var(--app-text)' }}>Умный импорт данных</h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: 'var(--app-text-secondary)' }}>
                  Загрузите свои Excel-таблицы, Word-документы или текстовые заметки — ИИ автоматически распознает клиентов, расписание и расходы. Переезд на ProPraxis за минуту, без ручного ввода.
                </p>
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  <span className="flex items-center gap-1"><Check size={12} style={{ color: 'var(--app-accent)' }} /> xlsx, docx, txt, csv</span>
                  <span className="flex items-center gap-1"><Check size={12} style={{ color: 'var(--app-accent)' }} /> Распознаёт свободный текст</span>
                  <span className="flex items-center gap-1"><Shield size={12} style={{ color: 'var(--app-accent)' }} /> Данные в РФ (YandexGPT)</span>
                </div>
              </div>
              {/* CTA */}
              <div className="shrink-0">
                <Button onClick={handleCTA} className="text-white" style={{ background: 'var(--app-accent)' }}>
                  Попробовать <ArrowRight size={16} className="ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features — asymmetric bento */}
      <section id="features" className="py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12 max-w-xl">
            <h2 className="font-heading mb-3" style={{ color: 'var(--app-text)', fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: 1.1, letterSpacing: '-0.01em' }}>
              Всё, что нужно<br />для практики
            </h2>
            <p className="text-base" style={{ color: 'var(--app-text-muted)' }}>Шесть инструментов вместо десяти приложений.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {FEATURES.map((f, i) => {
              const wide = i === 0 || i === 3 || i === 4;       // flagship + two accents → asymmetric rhythm
              const flagship = i === 0;
              return (
                <div key={i}
                  className={`group rounded-2xl border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-0.5 ${wide ? 'lg:col-span-2' : ''} ${wide ? 'flex flex-col lg:flex-row lg:items-center gap-5' : 'flex flex-col'}`}
                  style={{
                    background: flagship ? 'var(--app-accent-muted)' : 'var(--app-card)',
                    borderColor: flagship ? 'var(--app-accent)' : 'var(--app-border)',
                  }}>
                  <div className={`shrink-0 rounded-xl flex items-center justify-center ${wide ? 'w-14 h-14' : 'w-12 h-12 mb-4'}`}
                    style={{ background: flagship ? 'var(--app-accent)' : 'var(--app-accent-muted)' }}>
                    <f.icon size={wide ? 28 : 24} strokeWidth={1.8} style={{ color: flagship ? '#fff' : 'var(--app-accent)' }} />
                  </div>
                  <div className={wide ? 'lg:flex-1' : ''}>
                    <h3 className="font-heading mb-2" style={{ color: 'var(--app-text)', fontSize: wide ? '1.375rem' : '1.0625rem' }}>{f.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--app-text-secondary)' }}>{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialists */}
      {specialists.length > 0 && (
        <section className="py-20 px-4 sm:px-6 overflow-hidden" style={{ background: 'var(--app-card)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl mb-3" style={{ color: 'var(--app-text)' }}>Наши специалисты</h2>
              <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Уже работают с ProPraxis</p>
            </div>
          </div>
          {/* Infinite marquee */}
          <Marquee items={specialists} />
        </section>
      )}

      {/* Trust — committed band, placed before the ask */}
      <section className="py-20 px-4 sm:px-6" style={{ background: 'var(--app-sidebar)' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="w-14 h-14 rounded-2xl mb-5 flex items-center justify-center" style={{ background: 'var(--app-accent)' }}>
              <Shield size={28} className="text-white" />
            </div>
            <h2 className="font-heading mb-4" style={{ color: 'var(--app-text)', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: 1.1 }}>
              Данные клиентов<br />под защитой
            </h2>
            <p className="text-base leading-relaxed" style={{ color: 'var(--app-text-secondary)' }}>
              Файлы и персональные данные шифруются двумя независимыми слоями: AES-256 на уровне приложения перед отправкой и серверное шифрование (KMS) в хранилище. Даже при доступе к серверу данные останутся нечитаемыми.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {['AES-256-GCM шифрование', 'HttpOnly Secure cookies', 'CSRF-защита', 'Серверы в РФ (152-ФЗ)'].map((t, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border px-4 py-3.5" style={{ background: 'var(--app-card)', borderColor: 'var(--app-border)' }}>
                <Check size={16} className="shrink-0" style={{ color: 'var(--app-accent)' }} />
                <span className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-4 sm:px-6" style={{ background: 'var(--app-card)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="mb-12 max-w-xl">
            <h2 className="font-heading mb-3" style={{ color: 'var(--app-text)', fontSize: 'clamp(1.875rem, 4vw, 2.75rem)', lineHeight: 1.1, letterSpacing: '-0.01em' }}>Простые тарифы</h2>
            <p className="text-base" style={{ color: 'var(--app-text-muted)' }}>Начните бесплатно. Обновите, когда будете готовы.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {selfServeTiers.map((t) => (
              <div key={t.id} className="relative rounded-2xl border p-6 flex flex-col transition-colors"
                style={{ background: 'var(--app-card)', borderColor: t.popular ? 'var(--app-accent)' : 'var(--app-border)', marginTop: t.popular ? '-0.5rem' : undefined }}>
                {t.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white"
                    style={{ background: 'var(--app-accent)' }}>Популярный</div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <t.icon size={18} className={t.color} />
                  <h3 className={`font-heading text-lg ${t.color}`}>{t.name}</h3>
                </div>
                <p className="text-sm mb-4" style={{ color: 'var(--app-text-muted)' }}>{t.desc}</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-3xl font-bold" style={{ color: 'var(--app-text)' }}>{t.price}</span>
                  {t.period && <span className="text-sm" style={{ color: 'var(--app-text-muted)' }}>{t.period}</span>}
                </div>
                <ul className="space-y-2.5 mb-6 flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      {f.ok
                        ? <Check size={15} className="shrink-0" style={{ color: 'var(--app-accent)' }} />
                        : <X size={15} className="shrink-0" style={{ color: 'var(--app-text-faint)' }} />}
                      <span style={{ color: f.ok ? 'var(--app-text-secondary)' : 'var(--app-text-faint)' }}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={handleCTA}
                  className="w-full text-sm font-semibold"
                  style={t.popular
                    ? { background: 'var(--app-accent)', color: '#fff' }
                    : { background: 'transparent', color: 'var(--app-accent)', border: '1.5px solid var(--app-accent)' }}>
                  {t.id === 'start' ? 'Начать бесплатно' : `Выбрать ${t.name}`}
                </Button>
              </div>
            ))}
          </div>

          {/* Enterprise tier — full-width band instead of a grid orphan */}
          {clinicTier && (
            <div className="mt-5 rounded-2xl border p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-6"
              style={{ background: 'var(--app-sidebar)', borderColor: 'var(--app-border)' }}>
              <div className="lg:flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <clinicTier.icon size={20} className={clinicTier.color} />
                  <h3 className={`font-heading text-xl ${clinicTier.color}`}>{clinicTier.name}</h3>
                  <span className="text-sm" style={{ color: 'var(--app-text-muted)' }}>· {clinicTier.price}</span>
                </div>
                <p className="text-sm mb-3" style={{ color: 'var(--app-text-secondary)' }}>{clinicTier.desc}</p>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {clinicTier.features.map((f, i) => (
                    <span key={i} className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                      <Check size={14} className="shrink-0" style={{ color: 'var(--app-accent)' }} /> {f.text}
                    </span>
                  ))}
                </div>
              </div>
              <Button onClick={() => setSalesOpen(true)}
                className="shrink-0 text-white text-sm font-semibold px-6" style={{ background: 'var(--app-accent)', height: '3rem' }}>
                Связаться с отделом продаж
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-10" style={{ color: 'var(--app-text)' }}>Частые вопросы</h2>
          <div className="space-y-2">
            {FAQ.map((item, i) => (
              <div key={i} className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--app-border)' }}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium transition-colors"
                  style={{ color: 'var(--app-text)', background: openFaq === i ? 'var(--app-card)' : 'transparent' }}>
                  {item.q}
                  <ChevronDown size={16} className={`shrink-0 ml-2 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                    style={{ color: 'var(--app-text-muted)' }} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm leading-relaxed" style={{ color: 'var(--app-text-secondary)', background: 'var(--app-card)' }}>
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA — drenched accent closer */}
      <section className="py-24 px-4 sm:px-6 text-center" style={{ background: 'var(--app-accent-hover)' }}>
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading mb-4 text-white" style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', lineHeight: 1.08, letterSpacing: '-0.01em' }}>
            Начните вести практику<br />по-новому
          </h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.92)' }}>
            30 секунд на регистрацию. Бесплатно. Без карты.
          </p>
          <Button size="lg" onClick={handleCTA}
            className="px-10 text-base font-semibold" style={{ background: '#fff', color: 'var(--app-accent-hover)', height: '3.25rem' }}>
            Создать аккаунт <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--app-border)', background: 'var(--app-card)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <img src="/logo.png" alt="" className="w-8 h-8 rounded-md object-cover" />
                <span className="font-heading text-base" style={{ color: 'var(--app-text)' }}>
                  <span style={{ color: 'var(--app-text-secondary)' }}>Pro</span><span style={{ color: 'var(--app-accent)' }}>Praxis</span>
                </span>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
                Карманный секретарь для частной практики. Расписание, клиенты, финансы и персональная визитка — всё в одном месте.
              </p>
            </div>
            {/* Requisites */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--app-text-secondary)' }}>Реквизиты</h4>
              <div className="space-y-1.5 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                <p>ИП Алякаев Камиль Рустамович</p>
                <p>ИНН 056114276774</p>
                <p>ОГРН 326774600115050</p>
              </div>
            </div>
            {/* Contacts */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--app-text-secondary)' }}>Контакты</h4>
              <div className="space-y-1.5 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                <p>Телефон: +7 (985) 312-94-07</p>
                <p>Адрес: г. Москва, ул. Кастанаевская 43к2</p>
                <a href="mailto:support@propraxis.ru" className="block hover:underline" style={{ color: 'var(--app-accent)' }}>support@propraxis.ru</a>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-2" style={{ borderColor: 'var(--app-border)' }}>
            <p className="text-xs" style={{ color: 'var(--app-text-faint)' }}>
              &copy; {new Date().getFullYear()} ProPraxis. Все права защищены.
            </p>
            <div className="flex gap-4 text-xs">
              <Link to="/privacy" className="hover:underline" style={{ color: 'var(--app-text-faint)' }}>Политика конфиденциальности</Link>
              <Link to="/offer" className="hover:underline" style={{ color: 'var(--app-text-faint)' }}>Публичная оферта</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Beta signup modal */}
      <Dialog open={betaModalOpen} onOpenChange={setBetaModalOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-md p-0 overflow-hidden">
          <div className="p-8">
            {betaSubmitted ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                  <Check size={32} style={{ color: 'var(--app-accent)' }} />
                </div>
                <h3 className="font-heading text-xl mb-2" style={{ color: 'var(--app-text)' }}>Вы в очереди!</h3>
                <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                  Основатель сервиса свяжется с вами, чтобы объяснить правила и дать доступ.
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <img src="/logo.png" alt="" className="w-12 h-12 rounded-lg object-cover mx-auto mb-3" />
                  {betaRecruiting ? (
                    <>
                      <h3 className="font-heading text-xl mb-2" style={{ color: 'var(--app-text)' }}>Присоединяйтесь к бета-тестированию</h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--app-text-secondary)' }}>
                        Официальная регистрация пока закрыта, но вы можете стать одним из первых пользователей ProPraxis.
                      </p>
                      <div className="mt-3 p-3 rounded-lg" style={{ background: 'var(--app-accent-muted)' }}>
                        <p className="text-sm font-medium" style={{ color: 'var(--app-accent)' }}>
                          Бета-тестерам — 3 месяца тарифа «Практик» бесплатно
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3 className="font-heading text-xl mb-2" style={{ color: 'var(--app-text)' }}>Закрытое бета-тестирование</h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--app-text-secondary)' }}>
                        Сейчас регистрация закрыта. Оставьте контакт — мы свяжемся с вами, когда откроем доступ.
                      </p>
                    </>
                  )}
                </div>
                <div className="space-y-3">
                  <Input value={betaName} onChange={e => setBetaName(e.target.value)}
                    placeholder="Ваше имя"
                    className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" />
                  <Input value={betaEmail} onChange={e => setBetaEmail(e.target.value)}
                    type="email" placeholder="Email"
                    className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" />
                  <Input value={betaPhone} onChange={e => setBetaPhone(e.target.value)}
                    type="tel" placeholder="Телефон"
                    className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" />
                  <Input value={betaTelegram} onChange={e => setBetaTelegram(e.target.value)}
                    placeholder="Telegram (@username)"
                    className="bg-stone-950/50 border-stone-800 text-stone-200 h-11" />
                  <p className="text-[10px]" style={{ color: 'var(--app-text-faint)' }}>Заполните хотя бы одно поле</p>
                  <Button onClick={submitBetaSignup} className="w-full h-11 text-white"
                    disabled={!betaEmail.trim() && !betaPhone.trim() && !betaTelegram.trim()}
                    style={{ background: 'var(--app-accent)' }}>
                    {betaRecruiting ? 'Присоединиться к бета-тесту' : 'Встать в очередь'}
                  </Button>
                </div>
                <p className="text-[10px] text-center mt-4" style={{ color: 'var(--app-text-faint)' }}>
                  {betaRecruiting
                    ? 'С вами свяжется основатель сервиса.'
                    : 'Мы не передаём данные третьим лицам.'}
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Support button */}
      <button
        data-testid="support-btn"
        onClick={() => setSupportOpen(true)}
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 group cursor-pointer"
        style={{ background: 'var(--app-accent)' }} title="Написать в поддержку">
        <HelpCircle size={22} className="text-white" />
        <span className="absolute right-14 whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          style={{ background: 'var(--app-card)', color: 'var(--app-text)', border: '1px solid var(--app-border)' }}>
          Написать в поддержку
        </span>
      </button>

      {/* Support modal */}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <div className="p-6 space-y-5">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                <HelpCircle size={28} style={{ color: 'var(--app-accent)' }} />
              </div>
              <h3 className="font-heading text-lg mb-1" style={{ color: 'var(--app-text)' }}>Поддержка ProPraxis</h3>
              <p className="text-xs" style={{ color: 'var(--app-text-secondary)' }}>Свяжитесь с нами любым удобным способом</p>
            </div>
            <div className="space-y-3">
              <a
                data-testid="support-email-link"
                href="mailto:support@propraxis.ru"
                className="flex items-center gap-3 p-3 rounded-lg border transition-colors hover:border-amber-700/50"
                style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--app-accent-muted)' }}>
                  <Mail size={18} style={{ color: 'var(--app-accent)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>Почта</p>
                  <p className="text-xs" style={{ color: 'var(--app-accent)' }}>support@propraxis.ru</p>
                </div>
              </a>
              <a
                data-testid="support-telegram-link"
                href="https://t.me/elbulm"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border transition-colors hover:border-sky-700/50"
                style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)' }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-sky-900/30">
                  <Send size={18} className="text-sky-400" />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>Telegram</p>
                  <p className="text-xs text-sky-400">@elbulm</p>
                </div>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sales Contact Modal */}
      <Dialog open={salesOpen} onOpenChange={setSalesOpen}>
        <DialogContent className="max-w-sm" style={{ background: 'var(--app-card)', border: '1px solid var(--app-border)', color: 'var(--app-text)' }}>
          <div className="p-6 space-y-5">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: 'var(--app-accent-muted)' }}>
                <Building2 size={28} style={{ color: 'var(--app-accent)' }} />
              </div>
              <h3 className="font-heading text-lg mb-1" style={{ color: 'var(--app-text)' }}>Тариф «Организация»</h3>
              <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Оставьте контакты — мы свяжемся с вами</p>
            </div>
            {salesSubmitted ? (
              <div className="text-center py-4">
                <CheckCircle size={32} className="mx-auto mb-2" style={{ color: 'var(--app-accent)' }} />
                <p className="text-sm" style={{ color: 'var(--app-text-secondary)' }}>Спасибо! Мы свяжемся с вами в ближайшее время.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Input value={salesForm.name} onChange={e => setSalesForm(f => ({...f, name: e.target.value}))}
                  placeholder="Ваше имя" style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} />
                <Input value={salesForm.phone} onChange={e => setSalesForm(f => ({...f, phone: e.target.value}))}
                  placeholder="Телефон" style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} />
                <Input value={salesForm.email} onChange={e => setSalesForm(f => ({...f, email: e.target.value}))}
                  placeholder="Email (необязательно)" style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }} />
                <Button disabled={!salesForm.name || !salesForm.phone} className="w-full h-11 text-white" style={{ background: 'var(--app-accent)' }}
                  onClick={async () => {
                    try { await api.post('/public/sales-contact', salesForm); setSalesSubmitted(true); }
                    catch { toast.error('Ошибка отправки'); }
                  }}>
                  Отправить заявку
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cookie consent banner */}
      {!cookieConsent && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4" style={{ background: 'var(--app-sidebar)' }}>
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs leading-relaxed" style={{ color: 'var(--app-text-secondary)' }}>
              Мы используем файлы cookie для авторизации и работы сервиса. Продолжая использовать ProPraxis, вы соглашаетесь с{' '}
              <span style={{ color: 'var(--app-accent)' }}>
                <Link to="/privacy" className="hover:underline">политикой конфиденциальности</Link>
              </span>.
            </p>
            <Button size="sm" className="text-white shrink-0" style={{ background: 'var(--app-accent)' }}
              onClick={() => { localStorage.setItem('pp_cookie_consent', 'true'); setCookieConsent(true); }}>
              Принимаю
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


function Marquee({ items }) {
  const arr = Array.isArray(items) ? items : [];
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const offsetRef = useRef(0);
  const pausedRef = useRef(false);
  const singleWidthRef = useRef(0);

  useEffect(() => {
    // Measure once the first copy renders
    const measure = () => {
      if (!innerRef.current) return;
      const children = innerRef.current.children;
      if (children.length < 2) return;
      // First child is the first copy wrapper
      singleWidthRef.current = children[0].offsetWidth;
    };
    measure();
    window.addEventListener('resize', measure);

    // Honor reduced-motion: leave the strip static instead of scrolling forever
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      return () => window.removeEventListener('resize', measure);
    }

    const speed = 0.4;
    let raf;
    const tick = () => {
      if (!pausedRef.current && singleWidthRef.current > 0) {
        offsetRef.current -= speed;
        if (Math.abs(offsetRef.current) >= singleWidthRef.current) {
          offsetRef.current += singleWidthRef.current;
        }
        if (innerRef.current) {
          innerRef.current.style.transform = `translate3d(${Math.round(offsetRef.current)}px, 0, 0)`;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [items]);

  const renderStrip = (keyPrefix) => (
    <div className="flex gap-5 shrink-0 pr-5" key={keyPrefix}>
      {arr.map((sp, i) => {
        const t = CARD_THEMES[sp.card_theme] || CARD_THEMES.forest;
        const avatarUrl = sp.avatar ? `${BASE}/api/avatars/${sp.avatar}` : '';
        return (
          <a key={`${keyPrefix}-${i}`} href={`/p/${sp.slug}`} className="shrink-0 w-56 rounded-xl border transition-shadow hover:shadow-lg hover:shadow-black/20"
            style={{ background: t.card, borderColor: t.border }}>
            <div className="h-16 rounded-t-xl relative" style={{ background: `linear-gradient(135deg, ${t.bg}, ${t.accent}30)` }}>
              <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full border-3 overflow-hidden shadow-md"
                style={{ borderColor: t.card, background: t.cardDeep }}>
                {avatarUrl ? <img src={avatarUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center"><span className="text-xl font-heading" style={{ color: t.accent }}>{(sp.name || '?')[0]}</span></div>}
              </div>
            </div>
            <div className="pt-10 pb-4 px-3 text-center">
              <p className="font-heading text-sm truncate" style={{ color: t.text }}>{sp.name}</p>
              {sp.specialization && <p className="text-[11px] mt-0.5 truncate" style={{ color: t.textSec }}>{sp.specialization}</p>}
              {sp.tags?.length > 0 && (
                <div className="flex flex-wrap justify-center gap-1 mt-2">
                  {(sp.tags || []).map((tag, j) => (
                    <span key={j} className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: t.accentMuted, color: t.accent }}>{tag}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 text-[11px] font-medium flex items-center justify-center gap-1" style={{ color: t.accent }}>
                Записаться <ArrowRight size={11} />
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );

  return (
    <div ref={wrapRef} className="relative overflow-hidden"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}>
      <div ref={innerRef} className="flex" style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}>
        {renderStrip('a')}
        {renderStrip('b')}
        {renderStrip('c')}
      </div>
    </div>
  );
}
